import { useEffect, useState } from "react";
import { Box, Button, Popover, TextField } from "@mui/material";
import { grey } from "@mui/material/colors";
import EventOutlined from "@mui/icons-material/EventOutlined";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";

// Presentational inline date field used in grid cells: shows a calendar icon
// plus the date as text (or a muted "mm/dd/yyyy" placeholder when unset), and
// opens a small popover with a single date input + Save/Cancel on click. The
// parent owns persistence — `onSave(ymd)` receives the picked YYYY-MM-DD (or
// "" when cleared). Mirrors the look of the effective-start-date cell.
const InlineDatePopoverField = ({
  value,
  onSave,
  label = "Date",
  placeholder = "mm/dd/yyyy",
  loading,
  textColor,
  // Optional custom trigger content (e.g. the combined Validity display) shown
  // in place of the default calendar-icon + date.
  renderTrigger,
}) => {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState(value || "");

  // Re-seed the draft from the current value whenever the popover (re)opens.
  useEffect(() => {
    if (anchor) setDraft(value || "");
  }, [anchor, value]);

  const handleSave = () => {
    onSave?.(draft);
    setAnchor(null);
  };

  return (
    <>
      <Flx
        ac
        g={0.5}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          height: "100%",
          width: "100%",
          cursor: "pointer",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {renderTrigger || (
          <>
            <EventOutlined
              sx={{ fontSize: 15, color: grey[500], flexShrink: 0 }}
            />
            <Txt
              sx={{
                fontSize: 12,
                color: value ? textColor || "#1A1A19" : grey[500],
                fontStyle: value ? "normal" : "italic",
              }}
            >
              {value
                ? formatDate(value, placeholder, "MM/dd/yyyy")
                : placeholder}
            </Txt>
          </>
        )}
      </Flx>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            border: "1px solid #e5e3df",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            mt: 0.5,
            minWidth: 260,
            p: 1.5,
          },
        }}
      >
        <Flx column g={1.5}>
          <Box>
            <Txt sx={{ fontSize: 11, fontWeight: 600, color: grey[700], mb: 0.5 }}>
              {label}
            </Txt>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
            />
          </Box>
          <Flx ac jb g={1}>
            <Button
              size="small"
              variant="text"
              onClick={() => setAnchor(null)}
              disabled={loading}
              sx={{ textTransform: "none", fontSize: "12px", color: grey[700] }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{ textTransform: "none", fontSize: "12px" }}
            >
              {loading ? "Saving…" : "Save"}
            </Button>
          </Flx>
        </Flx>
      </Popover>
    </>
  );
};

export default InlineDatePopoverField;
